<template>
  <div class="error-wrapper">
    <div class="logo-wrapper">
      <img :src="arcaneLogo" alt="Arcane Logo" class="logo" />
    </div>

    <div class="contact-wrapper">
      <BbBtn @click="onContactSupportClick">Contact support</BbBtn>
    </div>

    <div class="message-wrapper">
      <div class="message">{{ message.title }}</div>
      <div class="details">{{ message.message }}</div>
      <v-btn @click="message.buttonAction">{{ message.buttonText }}</v-btn>
    </div>

    <div v-if="(organizations || []).length" class="organization-wrapper">
      <OrganizationSelect />
    </div>
  </div>
</template>

<script>
import { useStore } from 'vuex';
import OrganizationSelect from '@/components/account/OrganizationSelect';
import { computed } from 'vue';
import { GetOrganizations } from '~/constants/getters';
import { Logout } from '~/constants/actions';
import { useRouter } from 'vue-router/dist/vue-router';
import { Routes } from '~/constants/routes';
import arcaneLogo from '~/assets/svg/Arcane-logo.svg';
import { supportURL } from '~/utils/account-utils';

export default {
  components: {
    OrganizationSelect,
  },

  layout: 'empty',

  props: ['statusCode'],

  setup(props) {
    const store = useStore();
    const router = useRouter();
    const organizations = computed(() => store.getters[`authStore/${GetOrganizations}`]);
    const logout = () => store.dispatch(`authStore/${Logout}`);
    const onContactSupportClick = () => {
      window.open(supportURL, '_blank').focus();
    };
    const message = computed(() => {
      const { statusCode = '' } = props;
      switch (statusCode) {
        case '403':
          return {
            title: `It looks like you don't have access to this organization.`,
            message: 'Contact the owner of the organization to be added as a member.',
            buttonText: 'Sign out',
            buttonAction: () => logout(),
          };

        case '403.1':
          return {
            title: `You have been removed from ${this.error.organizationName}.`,
            message:
              'Select another organization in the dropdown to the left or contact an owner/admin, if this was a mistake.',
            buttonText: 'Sign out',
            buttonAction: () => logout(),
          };

        case '404':
          return {
            title: `Oops! It's a 404.`,
            message: `We can't find the specified resource.`,
            buttonText: 'Go to conversation',
            buttonAction: () => router.push(Routes.Home),
          };

        case '410.1':
          return {
            title: 'Your invitation is no longer valid',
            message: `It might have already been used or expired. Contact the admin of the organization to get a new invitation or try to login to the organization.`,
            buttonText: 'Login',
            buttonAction: () => router.push(Routes.Login),
          };

        default:
          return {
            title: 'Oops! Something went wrong.',
            message: 'An error has occurred and we are working to fix the problem.',
            buttonText: 'Go to conversation',
            buttonAction: () => router.push(Routes.Home),
          };
      }
    });
    return {
      arcaneLogo,
      message,
      onContactSupportClick,
      organizations,
    };
  },
};
</script>

<style lang="scss" scoped>
.error-wrapper {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-template-rows: repeat(5, 1fr);
  grid-column-gap: 0px;
  grid-row-gap: 0px;
  width: 100vw;
  height: 100vh;

  .logo-wrapper {
    grid-area: 1 / 1 / 2 / 2;
    margin-top: $bb-spacing-5;
    margin-left: $bb-spacing-5;

    .logo {
      height: 38px;
      cursor: pointer;
    }
  }

  .contact-wrapper {
    grid-area: 1 / 5 / 2 / 6;
    display: flex;
    justify-content: flex-end;
    margin-top: $bb-spacing-5;
    margin-right: $bb-spacing-5;
  }

  .message-wrapper {
    grid-area: 3 / 1 / 4 / 6;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    padding: 0 $bb-spacing-5;
    text-align: center;

    .message {
      @include bb-titles-styles('lg');
      margin-bottom: $bb-spacing-4;
    }

    .details {
      @include bb-regular-text-styles('lg');
      margin-bottom: $bb-spacing-6;
    }
  }

  .organization-wrapper {
    grid-area: 5 / 1 / 6 / 2;
    display: flex;
    align-items: flex-end;
    margin-left: $bb-spacing-5;
    margin-bottom: $bb-spacing-6;
    width: 248px;
  }
}
</style>
